import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Cpu,
  Sparkles,
  Layers,
  Bot,
  Palette,
  Terminal,
  Boxes,
  ArrowRight,
  Zap,
  Radio,
  Hexagon,
  Send,
  ChevronDown,
} from "lucide-react";
import HexScene from "./HexScenes";
import "./hex.css";

/* ------------------------------------------------------------
   Boot sequence lines (typewriter output for hero)
------------------------------------------------------------ */
const BOOT_LINES = [
  "[BIOS] hexawebos v3.1.0 — initializing kernel...",
  "[SYS ] mounting /system/core ................... [ ok ]",
  "[SYS ] loading neural interface ................ [ ok ]",
  "[NET ] establishing quantum uplink .............. [ ok ]",
  "[AI  ] injecting Novo agent ...................... [ ok ]",
  "[UI  ] rendering holographic layer .............. [ ok ]",
  "[SYS ] Welcome, operator. Ready.",
];

/* ------------------------------------------------------------
   Section IDs used for scrollspy + dock nav
------------------------------------------------------------ */
const SECTIONS = [
  { id: "hero", label: "boot" },
  { id: "core", label: "core" },
  { id: "modules", label: "modules" },
  { id: "grid", label: "grid" },
  { id: "stack", label: "stack" },
];

/* ============================================================
   1.  BOOT / HERO SECTION
   ============================================================ */
function BootHero({ onEnter, onLogin }) {
  const [lineIdx, setLineIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (lineIdx >= BOOT_LINES.length) return;
    const t = setTimeout(() => setLineIdx((i) => i + 1), 380);
    return () => clearTimeout(t);
  }, [lineIdx]);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 3200;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setProgress(p);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setReady(true);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <section id="hero" className="hex-section" data-testid="hex-hero">
      <div className="max-w-6xl w-full mx-auto grid md:grid-cols-2 gap-10 items-center">
        {/* LEFT — brand + copy */}
        <div>
          <div className="mono text-[10px] tracking-[0.4em] uppercase text-cyan-300/70 mb-6 flex items-center gap-3">
            <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
            node://hexa-01 · session-boot
          </div>

          {/* Wordmark (icon lives center-stage in the 3D scene behind) */}
          <h1
            data-testid="hex-hero-wordmark"
            className="font-bold leading-[0.9] tracking-tight"
            style={{
              fontSize: "clamp(56px, 8vw, 112px)",
              background: "linear-gradient(180deg, #ffffff 0%, #c4b5fd 55%, #38bdf8 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              letterSpacing: "-0.02em",
            }}
          >
            HEXA
          </h1>
          <div className="mono text-[11px] tracking-[0.42em] uppercase text-violet-300/80 mt-1">
            AI Web Operating System
          </div>

          <div className="mt-4 flex items-center gap-3 max-w-md">
            <div className="hex-line flex-1" />
            <span className="mono text-[9px] tracking-[0.35em] uppercase text-violet-300/60">v3.1</span>
            <div className="hex-line flex-1" />
          </div>

          <p className="mt-8 text-[15px] md:text-[17px] leading-relaxed text-slate-300/85 max-w-xl">
            Not a website — a system. A cinematic web-native OS that fuses your
            mail, meetings, projects, code, and AI agents into a single{" "}
            <span className="text-cyan-300 hex-glow-cyan">neural fabric</span>.
            Boot in. Take command.
          </p>

          {/* Loading bar */}
          <div className="mt-10 max-w-md">
            <div className="flex items-center justify-between mono text-[10px] uppercase tracking-[0.32em] text-slate-400 mb-2">
              <span>{ready ? "system.ready" : "initializing hexawebos..."}</span>
              <span className="text-cyan-300">{Math.round(progress * 100)}%</span>
            </div>
            <div className="relative h-[3px] bg-white/5 overflow-hidden rounded-full">
              <div
                className="absolute inset-y-0 left-0 rounded-full"
                style={{
                  width: `${progress * 100}%`,
                  background: "linear-gradient(90deg, #8b5cf6, #38bdf8, #22d3ee)",
                  boxShadow: "0 0 16px rgba(56,189,248,0.7)",
                }}
              />
              {!ready && <div className="hex-shimmer absolute inset-0 overflow-hidden" />}
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap gap-4">
            <button
              onClick={onEnter}
              disabled={!ready}
              data-testid="hex-enter-btn"
              className="hex-btn"
              style={{ opacity: ready ? 1 : 0.45 }}
            >
              <Zap size={14} /> Enter System <ArrowRight size={14} />
            </button>
            <button
              onClick={onLogin}
              data-testid="hex-signin-btn"
              className="hex-btn ghost"
            >
              <Terminal size={14} /> Sign In
            </button>
          </div>
        </div>

        {/* RIGHT — boot terminal */}
        <div className="hex-glass-cyan relative p-6 md:p-8 rounded-sm">
          <span className="hex-bracket tl" />
          <span className="hex-bracket tr" />
          <span className="hex-bracket bl" />
          <span className="hex-bracket br" />

          <div className="flex items-center justify-between mb-5">
            <div className="mono text-[10px] tracking-[0.3em] uppercase text-cyan-300/80 flex items-center gap-2">
              <Radio size={11} /> hexa/boot.log
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="w-2 h-2 bg-amber-400 rounded-full" />
              <span className="w-2 h-2 bg-slate-600 rounded-full" />
            </div>
          </div>

          <div className="mono text-[12.5px] leading-[1.9] text-slate-300 min-h-[240px]">
            {BOOT_LINES.slice(0, lineIdx).map((ln, i) => (
              <div key={i}>
                <span className="text-cyan-400">›</span> {ln}
              </div>
            ))}
            {lineIdx < BOOT_LINES.length && (
              <div>
                <span className="text-cyan-400">›</span>
                <span className="hex-caret" />
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-white/5 grid grid-cols-3 gap-4">
            {[
              { k: "kernel", v: "v3.1.0" },
              { k: "agents", v: "12 live" },
              { k: "latency", v: "42ms" },
            ].map((s) => (
              <div key={s.k}>
                <div className="mono text-[9px] tracking-[0.3em] uppercase text-slate-500">
                  {s.k}
                </div>
                <div className="mono text-[13px] text-cyan-300 hex-glow-cyan mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-8 flex flex-col items-center gap-2 text-slate-500 mono text-[10px] tracking-[0.35em] uppercase">
        <span>scroll to enter</span>
        <ChevronDown size={16} className="animate-bounce" />
      </div>
    </section>
  );
}

/* ============================================================
   2.  CORE / ABOUT SECTION — hex core + holographic copy
   ============================================================ */
function CoreSection() {
  return (
    <section id="core" className="hex-section" data-testid="hex-core">
      <div className="max-w-6xl w-full mx-auto grid md:grid-cols-[1fr_1.2fr] gap-12 items-center">
        {/* Left — hex core visual */}
        <div className="relative">
          <div className="relative w-full aspect-square max-w-[440px] mx-auto">
            {/* Local dark bubble so it reads over the 3D background */}
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at center, rgba(4,6,15,0.75) 25%, rgba(4,6,15,0.35) 55%, transparent 75%)",
              }}
            />
            {/* Rotating rings */}
            {[420, 340, 260].map((size, i) => (
              <div
                key={i}
                className="hex-orbit-ring"
                style={{
                  width: size,
                  height: size,
                  animation: `hex-spin ${28 + i * 8}s linear ${i % 2 ? "reverse" : ""} infinite`,
                }}
              />
            ))}
            {/* Center hex */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
              style={{ width: 180, height: 200 }}
            >
              <div
                className="hex-clip flex items-center justify-center"
                style={{
                  width: "100%",
                  height: "100%",
                  background: "linear-gradient(140deg, rgba(56,189,248,0.4), rgba(139,92,246,0.4))",
                  boxShadow: "0 0 80px rgba(56,189,248,0.5)",
                }}
              >
                <Hexagon size={56} className="text-white" style={{ filter: "drop-shadow(0 0 10px #38bdf8)" }} />
              </div>
            </div>
          </div>
        </div>

        {/* Right — holographic label */}
        <div>
          <div className="mono text-[10px] tracking-[0.4em] uppercase text-violet-300/70 mb-4">
            /system/core.about
          </div>
          <h2
            className="font-bold leading-[1.05] mb-6"
            style={{ fontSize: "clamp(36px, 5vw, 60px)" }}
          >
            The <span className="hex-glow-cyan text-cyan-300">operating</span> system<br />
            for the <span className="hex-glow-violet text-violet-300">next</span> web.
          </h2>
          <p className="text-slate-300/85 text-[15px] leading-[1.8] max-w-xl">
            HexaWebOS is a browser-native operating environment. It runs your
            apps, your inbox, your calendar, your projects — all held together
            by an agentic AI core that reads context across every surface. No
            downloads. No lock-in. Just command.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {[
              { k: "24", u: "modules", c: "#38bdf8" },
              { k: "12+", u: "ai agents", c: "#a78bfa" },
              { k: "1", u: "workspace", c: "#22d3ee" },
            ].map((s) => (
              <div key={s.u} className="hex-glass p-4 rounded-sm relative">
                <span className="hex-bracket tl" />
                <span className="hex-bracket br" />
                <div className="mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
                  {s.u}
                </div>
                <div
                  className="text-3xl font-bold mt-1 mono"
                  style={{ color: s.c, textShadow: `0 0 20px ${s.c}88` }}
                >
                  {s.k}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   3.  MODULES / SERVICES — hex tile grid
   ============================================================ */
const MODULES = [
  {
    id: "web",
    icon: Layers,
    title: "Web Systems",
    tag: "01 // frontend.ops",
    desc: "Blazing SPAs, real-time dashboards, PWAs that install like native. Every pixel calibrated.",
    color: "#38bdf8",
  },
  {
    id: "ai",
    icon: Bot,
    title: "AI Engineering",
    tag: "02 // neural.stack",
    desc: "Agent orchestration, RAG pipelines, custom LLM fine-tunes — Novo-grade autonomy.",
    color: "#a78bfa",
  },
  {
    id: "auto",
    icon: Cpu,
    title: "Automation",
    tag: "03 // ops.runtime",
    desc: "Zap the manual work. Multi-step workflows across mail, docs, projects, and 3rd-party APIs.",
    color: "#22d3ee",
  },
  {
    id: "uiux",
    icon: Palette,
    title: "UI / UX Systems",
    tag: "04 // interface.lab",
    desc: "Design systems that scale. Motion, glass, hex-grids, holograms — this page is proof.",
    color: "#f0abfc",
  },
];

function ModulesSection() {
  return (
    <section id="modules" className="hex-section" data-testid="hex-modules">
      <div className="max-w-6xl w-full mx-auto">
        <div className="mono text-[10px] tracking-[0.4em] uppercase text-cyan-300/70 mb-3">
          /modules · services.grid
        </div>
        <h2 className="font-bold leading-[1.05] mb-3" style={{ fontSize: "clamp(36px, 5vw, 60px)" }}>
          Draggable <span className="hex-glow-cyan text-cyan-300">windows</span>. Real <span className="hex-glow-violet text-violet-300">work.</span>
        </h2>
        <p className="text-slate-400 text-[15px] max-w-2xl mb-14">
          Every service is a module — install, snap, and forget. Hover to
          expand, click to zoom in.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {MODULES.map((m) => {
            const Icon = m.icon;
            return (
              <div
                key={m.id}
                data-testid={`hex-module-${m.id}`}
                className="hex-glass hex-tile rounded-sm relative"
                style={{
                  borderColor: `${m.color}55`,
                  boxShadow: `0 0 30px -14px ${m.color}66`,
                }}
              >
                <span className="hex-bracket tl" style={{ borderColor: `${m.color}88` }} />
                <span className="hex-bracket br" style={{ borderColor: `${m.color}88` }} />

                <div className="flex items-start justify-between">
                  <div
                    className="hex-tile-badge"
                    style={{
                      background: `linear-gradient(140deg, ${m.color}66, ${m.color}22)`,
                      borderColor: `${m.color}88`,
                      boxShadow: `0 0 20px ${m.color}55`,
                    }}
                  >
                    <Icon size={22} className="text-white" />
                  </div>
                  <div className="mono text-[10px] tracking-[0.28em] uppercase text-slate-500">
                    {m.tag}
                  </div>
                </div>

                <div className="mt-6">
                  <h3
                    className="text-2xl font-semibold"
                    style={{ color: m.color, textShadow: `0 0 22px ${m.color}55` }}
                  >
                    {m.title}
                  </h3>
                  <p className="text-slate-300/80 text-[14px] leading-[1.75] mt-2 max-w-lg">
                    {m.desc}
                  </p>
                </div>

                <div className="mt-auto pt-6 flex items-center justify-between border-t border-white/5">
                  <div className="mono text-[10px] tracking-[0.3em] uppercase text-slate-500">
                    status · <span style={{ color: m.color }}>online</span>
                  </div>
                  <div className="mono text-[10px] tracking-[0.3em] uppercase text-slate-400 flex items-center gap-1.5 hover:text-white transition">
                    open <ArrowRight size={11} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   4.  PROJECTS GRID — hex nodes
   ============================================================ */
const PROJECTS = [
  { id: "01", name: "Orbital Mail", tag: "email-os", tint: "#38bdf8" },
  { id: "02", name: "Novo v2", tag: "agent-core", tint: "#a78bfa" },
  { id: "03", name: "Hex Sheets", tag: "ai-excel", tint: "#22d3ee" },
  { id: "04", name: "Boss Panel", tag: "team-mgr", tint: "#f0abfc" },
  { id: "05", name: "Neon Drive", tag: "obj-store", tint: "#facc15" },
  { id: "06", name: "GhostShare", tag: "screen-mux", tint: "#34d399" },
  { id: "07", name: "Signal Room", tag: "vid-mesh", tint: "#fb7185" },
];

function ProjectsGrid() {
  return (
    <section id="grid" className="hex-section" data-testid="hex-projects">
      <div className="max-w-6xl w-full mx-auto">
        <div className="mono text-[10px] tracking-[0.4em] uppercase text-violet-300/70 mb-3">
          /projects · data.grid
        </div>
        <h2 className="font-bold leading-[1.05] mb-3" style={{ fontSize: "clamp(36px, 5vw, 60px)" }}>
          A <span className="hex-glow-violet text-violet-300">galaxy</span> of shipped work.
        </h2>
        <p className="text-slate-400 text-[15px] max-w-xl mb-14">
          Every hex is a live project. Click a node to open the case-study
          window — vessels of code, chaos, and craft.
        </p>

        <div
          className="grid gap-6"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}
        >
          {PROJECTS.map((p, i) => (
            <div
              key={p.id}
              data-testid={`hex-project-${p.id}`}
              className="hex-node"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div
                className="hex-node-inner"
                style={{
                  borderColor: `${p.tint}66`,
                  background: `linear-gradient(140deg, ${p.tint}22, ${p.tint}11)`,
                  boxShadow: `0 0 20px ${p.tint}22`,
                }}
              >
                <div className="mono text-[9px] tracking-[0.3em] uppercase text-slate-500">
                  {p.id}
                </div>
                <div
                  className="text-[15px] font-semibold mt-1"
                  style={{ color: p.tint, textShadow: `0 0 12px ${p.tint}88` }}
                >
                  {p.name}
                </div>
                <div className="mono text-[9px] tracking-[0.25em] uppercase text-slate-500 mt-1">
                  {p.tag}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   5.  TECH STACK — orbit system
   ============================================================ */
const STACK_ORBITS = [
  { r: 130, speed: 22, items: ["React", "Node", "Python", "Mongo"] },
  { r: 200, speed: 34, items: ["Three.js", "R3F", "Tailwind", "GSAP", "FastAPI"] },
  { r: 275, speed: 46, items: ["OpenAI", "Claude", "Gemini", "Docker", "K8s", "Redis"] },
];

function StackSection() {
  return (
    <section id="stack" className="hex-section" data-testid="hex-stack">
      <div className="max-w-6xl w-full mx-auto grid md:grid-cols-2 gap-12 items-center">
        {/* orbit visualization */}
        <div className="relative w-full aspect-square max-w-[600px] mx-auto">
          {/* Local dark bubble so the orbit reads over the 3D background */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, rgba(4,6,15,0.85) 30%, rgba(4,6,15,0.55) 55%, transparent 75%)",
            }}
          />
          {STACK_ORBITS.map((orbit, i) => (
            <div
              key={i}
              className="absolute top-1/2 left-1/2"
              style={{
                width: orbit.r * 2,
                height: orbit.r * 2,
                marginLeft: -orbit.r,
                marginTop: -orbit.r,
                animation: `hex-spin ${orbit.speed}s linear ${i % 2 ? "reverse" : ""} infinite`,
              }}
            >
              <div
                className="absolute inset-0 rounded-full border border-dashed"
                style={{ borderColor: "rgba(139,92,246,0.18)" }}
              />
              {orbit.items.map((label, k) => {
                const angle = (k / orbit.items.length) * 360;
                return (
                  <div
                    key={label}
                    className="hex-orbit-node"
                    style={{
                      transform: `rotate(${angle}deg) translate(-50%, -50%) translateY(-${orbit.r}px)`,
                      transformOrigin: "0 0",
                    }}
                  >
                    <span
                      className="block"
                      style={{ transform: `rotate(-${angle}deg)` }}
                    >
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
          {/* Center */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
            style={{
              width: 96,
              height: 108,
            }}
          >
            <div
              className="hex-clip flex items-center justify-center w-full h-full"
              style={{
                background: "linear-gradient(140deg, #38bdf8, #8b5cf6)",
                boxShadow: "0 0 60px rgba(56,189,248,0.6)",
              }}
            >
              <Sparkles size={30} className="text-white" />
            </div>
          </div>
        </div>

        <div>
          <div className="mono text-[10px] tracking-[0.4em] uppercase text-cyan-300/70 mb-3">
            /stack · tech.mesh
          </div>
          <h2 className="font-bold leading-[1.05] mb-6" style={{ fontSize: "clamp(36px, 5vw, 60px)" }}>
            Every tech <span className="hex-glow-cyan text-cyan-300">in orbit</span>.
          </h2>
          <p className="text-slate-300/85 text-[15px] leading-[1.8] max-w-lg">
            React on the front, FastAPI on the back, WebGL for the soul, and a
            trio of frontier LLMs whispering into every module. It&apos;s not a
            stack — it&apos;s a mesh, and everything talks.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
            {[
              "React 19", "React Three Fiber", "FastAPI", "MongoDB",
              "GSAP + Framer", "Tailwind", "Emergent LLM", "PWA-Ready",
            ].map((t) => (
              <div
                key={t}
                className="hex-glass px-4 py-2.5 rounded-sm mono text-[11px] tracking-[0.14em] text-slate-300"
              >
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   6.  FOOTER (was Terminal — removed per user request)
   ============================================================ */
// eslint-disable-next-line no-unused-vars
function FooterSection({ onEnter }) {
  return (
    <section id="footer" className="hex-section" data-testid="hex-footer-section" style={{ minHeight: "auto", paddingTop: 80, paddingBottom: 80 }}>
      <div className="max-w-4xl w-full mx-auto">
        <div className="mono text-[10px] tracking-[0.4em] uppercase text-cyan-300/70 mb-3">
          /connect · hexa.footer
        </div>
        <h2 className="font-bold leading-[1.05] mb-3" style={{ fontSize: "clamp(30px, 4.5vw, 52px)" }}>
          Ready to <span className="hex-glow-cyan text-cyan-300">boot in</span>?
        </h2>
        <p className="text-slate-400 text-[15px] max-w-xl mb-8">
          One identity, every app. Zero installs. Talk to us at{" "}
          <a href="mailto:team@hexawebos.com" className="text-cyan-300 hover:text-white transition">
            team@hexawebos.com
          </a>
          .
        </p>

        {/* Footer meta */}
        <div className="mt-6 pt-8 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
          <div className="mono text-[10px] tracking-[0.3em] uppercase text-slate-500">
            © hexawebos · 2026 · all systems nominal
          </div>
          <div className="mono text-[10px] tracking-[0.3em] uppercase text-slate-500 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
            uplink · stable
          </div>
        </div>

        {/* Legal / info nav */}
        <div
          className="mt-6 pt-6 border-t border-white/5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
          data-testid="hex-footer-legal"
        >
          <a href="/privacy" data-testid="footer-link-privacy"
             className="mono text-[11px] tracking-[0.28em] uppercase text-slate-400 hover:text-cyan-300 transition">privacy policy</a>
          <a href="/terms" data-testid="footer-link-terms"
             className="mono text-[11px] tracking-[0.28em] uppercase text-slate-400 hover:text-cyan-300 transition">terms &amp; conditions</a>
          <a href="/faq" data-testid="footer-link-faq"
             className="mono text-[11px] tracking-[0.28em] uppercase text-slate-400 hover:text-cyan-300 transition">faq</a>
          <a href="/blog" data-testid="footer-link-blog"
             className="mono text-[11px] tracking-[0.28em] uppercase text-slate-400 hover:text-cyan-300 transition">blog</a>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   NAVIGATION DOCK — floating pill
   ============================================================ */
function NavDock({ active, onNav, onLogin, onEnter }) {
  return (
    <div className="hex-dock" data-testid="hex-dock">
      <div className="mono text-[10px] tracking-[0.28em] uppercase text-cyan-300/80 px-3 flex items-center gap-2">
        <Hexagon size={12} className="text-cyan-400" />
        HEXA/OS
      </div>
      <div className="w-px h-4 bg-white/10 mx-1" />
      {SECTIONS.map((s) => (
        <button
          key={s.id}
          data-testid={`hex-nav-${s.id}`}
          onClick={() => onNav(s.id)}
          className={`hex-dock-item ${active === s.id ? "active" : ""}`}
        >
          {s.label}
        </button>
      ))}
      <div className="w-px h-4 bg-white/10 mx-1" />
      <button data-testid="hex-nav-signin" onClick={onLogin} className="hex-dock-item">sign in</button>
      <button
        data-testid="hex-nav-enter"
        onClick={onEnter}
        className="hex-dock-item"
        style={{
          background: "linear-gradient(120deg, rgba(139,92,246,0.4), rgba(56,189,248,0.35))",
          color: "#fff",
        }}
      >
        enter →
      </button>
    </div>
  );
}

/* ============================================================
   SIDE TELEMETRY RAILS
   ============================================================ */
function TelemetryRails() {
  return (
    <>
      <div className="hex-rail l">
        <div
          className="mono text-[9px] tracking-[0.4em] uppercase text-slate-500"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          hexa · node://0x1a · sync
        </div>
      </div>
      <div className="hex-rail r">
        <div
          className="mono text-[9px] tracking-[0.4em] uppercase text-cyan-300/70"
          style={{ writingMode: "vertical-rl" }}
        >
          system.online · sess-01
        </div>
      </div>
    </>
  );
}

/* ============================================================
   ROOT COMPONENT — pluggable in place of MarketingLanding
   ============================================================ */
export default function HexaExperience({ onLogin, onRegister }) {
  const rootRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const [active, setActive] = useState("hero");
  const [scrollDim, setScrollDim] = useState(0); // 0..1

  // Mouse tracking for parallax
  useEffect(() => {
    const onMove = (e) => {
      mouseRef.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouseRef.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // Scroll dim — fade the 3D canvas as we descend through system layers
  useEffect(() => {
    const container = rootRef.current;
    if (!container) return;
    const onScroll = () => {
      const y = container.scrollTop;
      const vh = window.innerHeight;
      // Fade from 0 at top to 0.75 by the third section
      const d = Math.min(0.75, y / (vh * 2.5));
      setScrollDim(d);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => container.removeEventListener("scroll", onScroll);
  }, []);

  // Scrollspy — IntersectionObserver against each section
  useEffect(() => {
    const container = rootRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { root: container, threshold: 0.4 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const enter = () => (onRegister ? onRegister() : onLogin && onLogin());

  return (
    <div
      ref={rootRef}
      data-testid="hexa-experience"
      className="hex-root hex-scanlines hex-grain hex-vignette"
    >
      {/* Background layers */}
      <HexScene mouseRef={mouseRef} />

      {/* Ambient background wash */}
      <div
        className="fixed inset-0 z-[1] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 20% 15%, rgba(56,189,248,0.14), transparent 45%), radial-gradient(ellipse at 80% 85%, rgba(139,92,246,0.16), transparent 45%)",
        }}
      />

      {/* Scroll dimmer — fades the 3D layer as user descends */}
      <div
        className="fixed inset-0 z-[2] pointer-events-none"
        style={{
          background: "linear-gradient(180deg, rgba(0,0,60,0) 0%, rgba(0,0,80,0.92) 100%)",
          opacity: scrollDim,
          transition: "opacity 0.4s ease-out",
        }}
      />

      <TelemetryRails />
      <NavDock active={active} onNav={scrollTo} onLogin={onLogin} onEnter={enter} />

      {/* Skip / bypass */}
      <button
        onClick={() => onLogin && onLogin()}
        className="hex-skip"
        data-testid="hex-skip"
      >
        skip intro › sign in
      </button>

      {/* Sections layer */}
      <div className="hex-layer">
        <BootHero onEnter={enter} onLogin={onLogin} />
        <CoreSection />
        <ModulesSection />
        <ProjectsGrid />
        <StackSection />
        <FooterSection onEnter={enter} />
      </div>
    </div>
  );
}
